import * as jotai from 'jotai'
import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

type SubscriptionParams = {
  subscriptionId: number | string | undefined
  guestToken?: string | undefined
  token?: string | undefined
}

export async function create(
  projectId: number | string,
  payload: api.CreateSubscription,
): Promise<api.HttpResult<api.Subscription>> {
  const response = await api.client('POST /projects/:id/subscriptions', {
    body: payload,
    urlParams: { id: String(projectId) },
  })

  return response.next((success) => success.body)
}

export async function cancel(subscriptionId: number | string): Promise<api.HttpResult<api.Subscription>> {
  const response = await api.client('POST /subscriptions/:id/cancel', {
    urlParams: { id: String(subscriptionId) },
  })

  return response.next((success) => success.body)
}

export async function get(params: SubscriptionParams): Promise<api.HttpResult<api.Subscription>> {
  const response = await api.client('GET /subscriptions/:id', {
    urlParams: { id: String(params.subscriptionId) },
    getParams: { ...(params.guestToken && { token: params.guestToken }) },
    ...(!params.guestToken && params.token && { token: params.token }),
    withToken: params.guestToken ? false : true,
  })

  return response.next((success) => success.body)
}

const SHARED_SUBSCRIPTION_CACHE = jotai.atom<hooks.Cache<api.Subscription>>({})

export function useSubscriptionSharedQuery(params: SubscriptionParams): {
  response: hooks.Response<api.Subscription>
  query: () => void
} {
  const { query: querySubscription, getCurrentResponse } = hooks.useSharedQuery(get, {
    atom: SHARED_SUBSCRIPTION_CACHE,
    id,
  })

  return { response: getResponse(), query }

  function getResponse(): hooks.Response<api.Subscription> {
    if (params.subscriptionId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    const response = getCurrentResponse([params])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response as hooks.Response<api.Subscription>
  }

  function query(): Promise<void> | undefined {
    if (params.subscriptionId) return querySubscription(params)
  }
}

function id(params: SubscriptionParams): string {
  return JSON.stringify([params.subscriptionId, params.guestToken, params.token])
}
